/*
 * 파트너 문의/요청 패널 (lab 공용)
 *  - 시험일정총괄표의 문의 배지·상세 모달에 임베드해 목록 확인 + 즉시 답변
 *  - scheduleId 를 주면 해당 시험 건만, 없으면 전체
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  List, Tag, Button, Modal, Input, Space, Typography, Empty, Spin,
  message, Badge, Divider, Upload, Tooltip,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  MessageOutlined, SendOutlined, CheckCircleOutlined, ClockCircleOutlined,
  StopOutlined, PaperClipOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { inquiryAPI } from '../services/api';
import { gray, brand } from '../theme/tokens';
import { usePermission } from '../contexts/PermissionContext';
import { fmtDateTime } from '../utils/date';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const CONTENT_MAX = 2000;

export interface PanelAttachment {
  id: number; file_name: string; file_size: number;
  uploader_type: string; uploader_name: string;
}
export interface PanelReply {
  id: number; author_type: 'admin' | 'partner'; author_name: string;
  content: string; created_at: string; attachments?: PanelAttachment[];
}
export interface PanelInquiry {
  id: number; schedule_id?: number; test_number: string; schedule_title: string;
  company_name: string; company_code: string; partner_username: string;
  inquiry_type: string; title: string; content: string; status: string;
  admin_unread: boolean; reply_count: number; attachment_count?: number;
  created_at: string; last_reply_at?: string;
  attachments?: PanelAttachment[]; replies?: PanelReply[];
}

export const ATTACH_MAX_BYTES = 20 * 1024 * 1024;

/** 드롭된 File[] → antd fileList 항목 (용량 초과분은 제외하고 안내) */
export const filesToUploadItems = (files: File[]): UploadFile[] => {
  const items: UploadFile[] = [];
  files.forEach((file, i) => {
    if (file.size > ATTACH_MAX_BYTES) {
      message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
      return;
    }
    items.push({
      uid: `drop-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      originFileObj: file as any,
    });
  });
  return items;
};

export const INQUIRY_STATUS_META: Record<string, { color: string; icon: React.ReactNode }> = {
  '대기':     { color: 'orange',  icon: <ClockCircleOutlined /> },
  '답변완료': { color: 'green',   icon: <CheckCircleOutlined /> },
  '수용':     { color: 'blue',    icon: <CheckCircleOutlined /> },
  '반려':     { color: 'red',     icon: <StopOutlined /> },
  '종료':     { color: 'default', icon: <StopOutlined /> },
};

/* 일시 표기는 공용 유틸이 정본 — 여기서 다시 만들지 않는다 (utils/date.ts) */
export { fmtDateTime };

export const fmtBytes = (n = 0) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

interface Props {
  scheduleId?: number;
  /** 목록/답변 변경 시 상위 배지 갱신 */
  onChanged?: () => void;
}

const InquiryPanel: React.FC<Props> = ({ scheduleId, onChanged }) => {
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('inquiries', 'WRITE');

  const [items, setItems] = useState<PanelInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<PanelInquiry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState<UploadFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inquiryAPI.list(scheduleId ? { schedule_id: scheduleId } : undefined);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: number) => {
    setDetail({ id } as PanelInquiry);
    setDetailLoading(true);
    setReplyText('');
    setReplyFiles([]);
    try {
      const res = await inquiryAPI.detail(id);
      setDetail(res.data);
      setItems(prev => prev.map(x => (x.id === id ? { ...x, admin_unread: false } : x)));
      onChanged?.();
    } catch {
      message.error('문의를 불러오지 못했습니다.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const download = async (a: PanelAttachment) => {
    setDownloading(a.id);
    try {
      const res = await inquiryAPI.downloadFile(a.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = a.file_name;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('다운로드에 실패했습니다.');
    } finally {
      setDownloading(null);
    }
  };

  const renderAttachments = (list?: PanelAttachment[]) => {
    if (!list || list.length === 0) return null;
    return (
      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {list.map(a => (
          <Space key={a.id} size={4}
            style={{ background: '#fff', border: `1px solid ${gray[200]}`, borderRadius: 8, padding: '3px 8px', fontSize: 12 }}>
            <PaperClipOutlined style={{ color: gray[400], fontSize: 12 }} />
            <Tooltip title={`${a.file_name} · ${fmtBytes(a.file_size)} · ${a.uploader_name}`}>
              <span onClick={() => download(a)}
                style={{ cursor: 'pointer', color: brand.primary, maxWidth: 220, overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}>
                {a.file_name}
              </span>
            </Tooltip>
            <span style={{ color: gray[300], fontSize: 11 }}>{fmtBytes(a.file_size)}</span>
            <Button type="text" size="small" loading={downloading === a.id}
              icon={<DownloadOutlined style={{ fontSize: 12 }} />} onClick={() => download(a)} />
          </Space>
        ))}
      </div>
    );
  };

  const submitReply = async () => {
    if (!detail || !replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await inquiryAPI.reply(detail.id, replyText.trim());
      const newReplyId = (res.data.replies || []).slice(-1)[0]?.id;
      const failed: string[] = [];
      for (const f of replyFiles) {
        try {
          await inquiryAPI.uploadFile(detail.id, (f.originFileObj || f) as File, newReplyId);
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) message.warning(`답변은 등록되었으나 첨부 실패: ${failed.join(', ')}`);
      else message.success('답변이 등록되었습니다. 파트너사에 알림이 전달됩니다.');
      setReplyText('');
      setReplyFiles([]);
      if (replyFiles.length) {
        const fresh = await inquiryAPI.detail(detail.id);
        setDetail(fresh.data);
      } else {
        setDetail(res.data);
      }
      load();
      onChanged?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '답변 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (status: string) => {
    if (!detail) return;
    try {
      const res = await inquiryAPI.updateStatus(detail.id, status);
      setDetail(res.data);
      message.success(`상태를 '${status}'(으)로 변경했습니다.`);
      load();
      onChanged?.();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '변경에 실패했습니다.');
    }
  };

  return (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 28 }}><Spin /></div>
      ) : items.length === 0 ? (
        <Empty description="접수된 문의/요청이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => {
            const meta = INQUIRY_STATUS_META[item.status] || INQUIRY_STATUS_META['대기'];
            const waiting = item.status === '대기';
            return (
              <List.Item
                onClick={() => openDetail(item.id)}
                style={{
                  cursor: 'pointer', padding: '10px 12px', borderRadius: 8, marginBottom: 6,
                  background: waiting ? '#fff7ed' : 'transparent',
                  border: `1px solid ${waiting ? '#fed7aa' : 'transparent'}`,
                }}
              >
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <Tag color={item.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ margin: 0, fontWeight: 600 }}>
                      {item.inquiry_type}
                    </Tag>
                    {item.admin_unread && <Badge dot color="#ef4444" />}
                    <span style={{ fontWeight: item.admin_unread ? 700 : 600, fontSize: 13, color: gray[700] }}>
                      {item.title}
                    </span>
                    <div style={{ marginLeft: 'auto' }}>
                      <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>{item.status}</Tag>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: gray[400], flexWrap: 'wrap' }}>
                    {item.company_name && <span>{item.company_code ? `[${item.company_code}] ` : ''}{item.company_name}</span>}
                    <span>작성자 {item.partner_username}</span>
                    <span>{fmtDateTime(item.created_at)}</span>
                    <span>답글 {item.reply_count}건</span>
                    {!!item.attachment_count && <span><PaperClipOutlined /> {item.attachment_count}</span>}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}

      {/* 스레드 + 답변 */}
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        width={680}
        title={
          <Space>
            <MessageOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700 }}>{detail?.title || '문의 상세'}</span>
            {detail?.status && (
              <Tag color={(INQUIRY_STATUS_META[detail.status] || INQUIRY_STATUS_META['대기']).color}>{detail.status}</Tag>
            )}
          </Space>
        }
        footer={
          <Space>
            {/* 요청은 조치 결과(수용/반려)로 닫아야 이력이 남는다 — 목록 화면과 동일 규칙 */}
            {canWrite && detail?.inquiry_type === '요청' && detail?.status !== '종료' && (
              <>
                <Button type="primary" ghost disabled={detail?.status === '수용'}
                  onClick={() => changeStatus('수용')}>수용 (반영완료)</Button>
                <Button danger disabled={detail?.status === '반려'}
                  onClick={() => changeStatus('반려')}>반려</Button>
              </>
            )}
            {canWrite && detail?.status !== '종료' && (
              <Button onClick={() => changeStatus('종료')}>
                {detail?.inquiry_type === '요청' ? '종료' : '문의 종료'}
              </Button>
            )}
            {canWrite && detail?.status === '종료' && (
              <Button onClick={() => changeStatus('답변완료')}>종료 취소</Button>
            )}
            <Button type="primary" onClick={() => setDetail(null)}>닫기</Button>
          </Space>
        }
        styles={{ body: { maxHeight: '62vh', overflowY: 'auto' } }}
      >
        {detailLoading || !detail?.created_at ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: gray[500], marginBottom: 10 }}>
              <Space size={10} wrap>
                {detail.company_code && <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>{detail.company_code}</Tag>}
                <span>{detail.company_name}</span>
                <span>작성자 <b style={{ color: gray[700] }}>{detail.partner_username}</b></span>
                {detail.test_number && <span>시험번호 <b style={{ color: brand.primary }}>{detail.test_number}</b></span>}
                <span>{fmtDateTime(detail.created_at)}</span>
              </Space>
            </div>

            <div style={{ background: '#f8fafc', border: `1px solid ${gray[200]}`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: gray[500], marginBottom: 6 }}>
                <Tag color={detail.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ marginRight: 6 }}>{detail.inquiry_type}</Tag>
                파트너사 문의
              </div>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{detail.content}</Paragraph>
              {renderAttachments(detail.attachments)}
            </div>

            {(detail.replies || []).map((r) => {
              const isAdmin = r.author_type === 'admin';
              return (
                <div key={r.id} style={{
                  background: isAdmin ? '#eef2ff' : '#f8fafc',
                  border: `1px solid ${isAdmin ? '#c7d2fe' : gray[200]}`,
                  borderRadius: 10, padding: 12, marginBottom: 10,
                  marginLeft: isAdmin ? 32 : 0, marginRight: isAdmin ? 0 : 32,
                }}>
                  <div style={{ fontSize: 11, color: isAdmin ? brand.primary : gray[500], marginBottom: 6, fontWeight: 600 }}>
                    {isAdmin ? `담당자 (${r.author_name})` : `파트너 (${r.author_name})`}
                    <span style={{ fontWeight: 400, marginLeft: 8, color: gray[400] }}>{fmtDateTime(r.created_at)}</span>
                  </div>
                  <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{r.content}</Paragraph>
                  {renderAttachments(r.attachments)}
                </div>
              );
            })}

            {canWrite && detail.status !== '종료' && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                {/* 작성 영역 어디에나 파일을 끌어다 놓으면 첨부된다 */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault(); setDragOver(false);
                    const dropped = Array.from(e.dataTransfer?.files || []);
                    if (dropped.length) setReplyFiles(prev => [...prev, ...filesToUploadItems(dropped)]);
                  }}
                  style={{
                    border: `2px dashed ${dragOver ? brand.primary : 'transparent'}`,
                    background: dragOver ? '#eef2ff' : 'transparent',
                    borderRadius: 8, padding: 6, transition: 'all .15s',
                  }}
                >
                  <TextArea
                    rows={4} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    placeholder="파트너사에 전달할 답변을 입력하세요. (파일을 이곳에 끌어다 놓으면 첨부됩니다)"
                    maxLength={CONTENT_MAX} showCount
                  />
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                    <Upload
                      multiple
                      fileList={replyFiles}
                      beforeUpload={(file) => {
                        if (file.size > ATTACH_MAX_BYTES) {
                          message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
                          return Upload.LIST_IGNORE;
                        }
                        return false;
                      }}
                      onChange={({ fileList: fl }) => setReplyFiles(fl)}
                      onRemove={(file) => setReplyFiles(prev => prev.filter(f => f.uid !== file.uid))}
                    >
                      <Button size="small" icon={<PaperClipOutlined />}>파일 첨부</Button>
                    </Upload>
                    <div style={{ marginLeft: 'auto' }}>
                      <Button type="primary" icon={<SendOutlined />} loading={submitting}
                        disabled={!replyText.trim()} onClick={submitReply}>
                        답변 등록
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
            {!canWrite && (
              <Text type="secondary" style={{ fontSize: 12 }}>답변 권한이 없습니다 (inquiries:WRITE 필요).</Text>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default InquiryPanel;
